<template>
  <div class="text-element relative flex flex-col self-stretch">
    <CalendarIcon
      v-if="element.type === 'date'"
      :style="{ top: iconTopPosition }"
      :class="`input-icon pointer-events-none absolute left-[12px] z-[1] h-[16px] !w-[16px] -translate-y-1/2`"
    />
    <input
      ref="inputElement"
      :value="inputValue"
      :placeholder="inputPlaceholder"
      class="form-placeholder form-control shadow-none focus:outline-transparent focus:ring-0"
      :class="classList"
      :style="{
        ...styleObj,
        ...(element.type === 'date' ? { paddingLeft: '36px' } : {}),
      }"
      @input="handleInput"
      @focus="handleFocus"
      @blur="handleBlur"
    />
    <template v-if="isActive">
      <div
        ref="shortLabelElement"
        :key="resetKey"
        contenteditable="true"
        class="short-label font-inter cursor-text border-0 bg-transparent p-0 text-sm shadow-none focus:outline-none"
        :data-placeholder="t('common.shortLabelPlaceholder')"
        @blur="updateShortLabel"
        @compositionstart="handleCompositionStart"
        @compositionend="compositionEndHandler"
        @input="handleInputLabel"
        @keydown="handleSafariCursor"
        @click="(e) => e.stopPropagation()"
        @focus="(e) => e.stopPropagation()"
        v-text="shortLabel"
      ></div>
    </template>
    <template v-else>
      <span v-if="element.shortLabel" class="short-label font-inter break-words text-sm">
        {{ element.shortLabel }}
      </span>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useIMEComposition } from "@/composition/useIMEComposition";
import { useAppStore } from "@/store/app";
import { focusInputElement } from "@/util/focusNavigation";
import {
  fixSafariCursor,
  handleShortLabelInput as handleShortLabelInputUtil,
} from "@/util/methods";
import { CalendarIcon } from "@gohighlevel/ghl-icons/24/outline";
import { nextTick, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const { handleCompositionStart, handleCompositionEnd, withIMECheck, resetKey } =
  useIMEComposition();
const props = defineProps({
  placeholder: {
    type: String,
    default: "",
  },
  classList: {
    type: Array,
    default: () => {
      return [];
    },
  },
  containerClass: {
    type: Array,
    default: () => {
      return [];
    },
  },
  styleObj: {
    type: Object,
    default: () => {
      return {};
    },
  },
  readonly: {
    type: Boolean,
    default: true,
  },
  element: {
    type: Object,
    default: () => {
      return {};
    },
  },
  isActive: {
    type: Boolean,
    default: false,
  },
});

watch(
  () => props.placeholder,
  (newVal) => {
    inputPlaceholder.value = newVal;
  }
);

const emit = defineEmits(["update:placeholder", "update:shortLabel"]);
const inputPlaceholder = ref(props.placeholder);
const inputValue = ref("");
const iconTopPosition = ref<string>("50%");
const shortLabel = ref(props.element.shortLabel || "");
const shortLabelElement = ref<HTMLElement | null>(null);
const inputElement = ref<HTMLInputElement | null>(null);

// Handler method to call the imported fixSafariCursor function
const handleSafariCursor = (e: Event) => {
  const isKeyDown = e.type === "keydown";
  fixSafariCursor(e, isKeyDown);
};

const rawInputLabelHandler = (e: Event) => {
  handleShortLabelInputUtil(e, shortLabel, updateShortLabel);
};

const compositionEndHandler = handleCompositionEnd(rawInputLabelHandler);
const handleInputLabel = withIMECheck(rawInputLabelHandler);

watch(
  () => props.element.shortLabel,
  (newVal) => {
    shortLabel.value = newVal || "";
  }
);

const store = useAppStore();

const updateIconPosition = () => {
  nextTick(() => {
    if (inputElement.value) {
      const inputHeight = inputElement.value.offsetHeight;
      const centerPosition = inputHeight / 2;
      iconTopPosition.value = `${centerPosition}px`;
    }
  });
};

// Watch for input height changes
watch(
  () => [props.styleObj, props.classList, inputElement.value],
  () => {
    updateIconPosition();
  },
  { deep: true }
);

// Watch for isActive changes (when short label appears/disappears)
watch(
  () => props.isActive,
  () => {
    updateIconPosition();
  }
);

onMounted(() => {
  updateIconPosition();
});

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  inputValue.value = target.value;
  inputPlaceholder.value = target.value;
  emit("update:placeholder", inputPlaceholder.value);
};

const handleFocus = (event: Event) => {
  const target = event.target as HTMLInputElement;
  // Use current value, otherwise fall back to placeholder text so selection is visible
  const nextValue = target.value || target.placeholder || inputPlaceholder.value;
  inputValue.value = nextValue;
  inputPlaceholder.value = nextValue;
  target.value = nextValue;
  focusInputElement(target);
};

const handleBlur = (event: Event) => {
  inputValue.value = "";
  store.addToHistory();
};

const updateShortLabel = () => emit("update:shortLabel", shortLabel.value);
</script>

<style scoped>
.short-label {
  background-color: transparent;
  margin-top: 4px;
  font-size: 14px;
  color: #4b5563;
}

.short-label:empty:before {
  content: attr(data-placeholder);
}
</style>
